<template>
  <div v-click-outside="() => { open = false }" class="header-tag-select">
    <div
      class="expander"
      :class="{ closed: closedUi && !internalValue.length && !search && !open }"
      :data-open="open"
    >
      <v-autocomplete
        v-model="internalValue"
        v-model:search="search"
        v-model:menu="open"
        :items="displayedTags"
        return-object
        multiple
        chips
        closable-chips
        clearable
        hide-details
        item-title="name"
        item-value="id"
        density="compact"
        variant="outlined"
        prepend-inner-icon="mdi-tag-multiple-outline"
        :menu-props="{ maxHeight: 100 }"
        :hide-no-data="true"
        class="expanding-select"
        :placeholder="$lang?.labels?.tags"
        @update:model-value="emitTags"
        @keydown.down="ensureOpen"
        @focus="open = true"
      >
        <template #item="{ props, item }">
          <v-list-item v-bind="props">
            <v-list-item-title :data-cy="item[itemValue]">
              {{ item[itemText] }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-autocomplete>
    </div>
  </div>
</template>

<script>
import { getTagsUsingGET as getTags } from '@/utils/api'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'HeaderTagSelect',
  props: {
    itemText: { type: String, default: 'name' },
    itemValue: { type: String, default: 'id' },
    minChars: { type: Number, default: 2 },
    size: { type: Number, default: 50 }
  },
  emits: ['update:tags', 'update:tagIds', 'change'],
  data() {
    return {
      internalValue: [],
      allOptions: [],
      search: '',
      searchResults: [],
      open: false,
      closedUi: true,
      debounceId: null
    }
  },
  computed: {
    ...mapGetters('app', ['tagHeaderFilter']),
    displayedTags() {
      return this.searchResults.length ? this.searchResults : this.allOptions
    }
  },
  watch: {
    'internalValue.length'(n) {
      this.closedUi = n === 0
    },
    search(val) {
      if (!val || val.length < this.minChars) {
        this.searchResults = []
        return
      }
      clearTimeout(this.debounceId)
      this.debounceId = setTimeout(this.fetchTags, 300)
    }
  },
  mounted() {
    if (this.tagHeaderFilter?.tags?.length) {
      this.internalValue = [...this.tagHeaderFilter.tags]
      this.mergeIntoPool(this.internalValue)
      this.closedUi = this.internalValue.length === 0

      const ids = this.internalValue.map(t => t[this.itemValue])
      this.$emit('update:tags', this.internalValue)
      this.$emit('update:tagIds', ids)
      this.$emit('change', this.internalValue)
    }
  },
  methods: {
    ...mapActions('app', ['saveTagHeaderFilter', 'clearTagHeaderFilter']),
    ensureOpen() { this.open = true },
    emitTags(val) {
      this.internalValue = Array.isArray(val) ? val : [val].filter(Boolean)
      this.mergeIntoPool(this.internalValue)

      const ids = this.internalValue.map(t => t[this.itemValue])
      this.$emit('update:tags', this.internalValue)
      this.$emit('update:tagIds', ids)
      this.$emit('change', this.internalValue)

      if (this.internalValue.length) {
        this.saveTagHeaderFilter({ tags: this.internalValue, ids })
      } else {
        this.clearTagHeaderFilter()
      }
    },
    mergeIntoPool(items) {
      items.forEach(tag => {
        if (tag && !this.allOptions.some(o => o[this.itemValue] === tag[this.itemValue])) {
          this.allOptions.push(tag)
        }
      })
    },
    async fetchTags() {
      try {
        const res = await getTags({
          name: this.search,
          size: this.size,
          orderField: 'name',
          orderDirection: 'ASC'
        })
        const items = res?.data?.data?.items || []
        const mapped = items.map(({ id, name }) => ({ id, name }))
        this.searchResults = mapped
        this.mergeIntoPool(mapped)
      } catch (err) {
        this.searchResults = []
      }
    }
  }
}
</script>

<style scoped lang="scss">
.header-tag-select {
  position: relative;

  /* Wrapper handles the width animation; autocomplete stays width:100% */
  .expander {
    transition: max-width .5s;
    width: 380px;
    max-width: 380px;
    margin-left: 12px;

    &.closed { max-width: 50px; }

    /* Optional: freeze the transition while the menu is open to avoid RO spam */
    &[data-open="true"] { transition: none; }
  }

  .expanding-select { width: 100%; }   /* no transitions here */

  ::v-deep(.v-field--active .v-icon) {
    color: rgb(var(--v-theme-primary)) !important;
    caret-color: rgb(var(--v-theme-primary)) !important;
  }
}

::v-deep(.v-select__menu-icon),
::v-deep(.v-field__append-inner .v-icon) {
  display: none !important;
}

@media (max-width: 890px) {
  .header-tag-select { display: none; }
}
</style>
